import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from 'preact-iso';
import MojitoCore from 'mojito/core';
import { mapValues } from 'mojito/utils';

const StringUtils = MojitoCore.Base.StringUtils;
const { arraysAreEqual } = MojitoCore.Base.objUtils;
const { useAppContext } = MojitoCore.Presentation.Hooks;

/**
 * Presentation related hooks.
 *
 * @name Hooks
 * @memberof Mojito.Presentation
 */

/**
 * Hook returns the previous value of a prop or state.
 *
 * @function usePreviousValue
 *
 * @param {*} value - Value to track; returns the previous value from the previous render.
 * @returns {*} Previous value, or undefined on the first render.
 * @memberof Mojito.Presentation.Hooks
 */
export const usePreviousValue = value => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

/**
 * Hook utilises setTimeout usage.
 *
 * @function useTimeout
 *
 * @param {number} delay - Number of milliseconds to delay. If set to -1 the timeout will be resolved immediately.
 * @param {Function} done - Done callback triggered once setTimeout is over. This function will be cached with useCallback, use deps parameter to invalidate the cache.
 * @param {Array} [deps = []] - List of done callback dependencies.
 *
 * @returns {{startTimer: Function, stopTimer: Function, isRunning: boolean}} Object that contains functions to start
 * and stop the timer and to check if timer is currently running.
 * @memberof Mojito.Presentation.Hooks
 */
export const useTimeout = (delay, done, deps = []) => {
    const timeoutRef = useRef();
    const isRunning = useRef(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const doneCallback = useCallback(() => done(), deps);

    const stopTimer = useCallback(() => {
        clearTimeout(timeoutRef.current);
        isRunning.current = false;
    }, []);

    const startTimer = useCallback(() => {
        stopTimer();
        if (delay > -1) {
            timeoutRef.current = setTimeout(() => {
                isRunning.current = false;
                doneCallback();
            }, delay);
            isRunning.current = true;
        } else {
            isRunning.current = false;
            doneCallback();
        }
    }, [delay, stopTimer, doneCallback]);

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    return { startTimer, stopTimer, isRunning: isRunning.current };
};

/**
 * Hook sanitize html string for possible XSS vulnerabilities.
 *
 * @function useSanitizeHtml
 *
 * @param {string} html - HTML string to be sanitized.
 *
 * @returns {string} Sanitized string.
 * @memberof Mojito.Presentation.Hooks
 */
export const useSanitizeHtml = html => {
    const [sanitized, setSanitized] = useState();
    useEffect(() => {
        StringUtils.sanitizeHtml(html)
            .then(setSanitized)
            .catch(() => setSanitized(undefined));
    }, [html]);
    return sanitized;
};

/**
 * Hook produces comparator function helpful to compare array instance between render cycles.
 * Compare function accepts array and checks it towards array that was provided during previous render. Returns boolean.
 * If true then array differs from the one that was used previously, false otherwise.
 *
 * @function useArrayComparator
 *
 * @param {Array} initialArray - Initial array instance.
 *
 * @returns {Function} Compare function that accepts instance of array and returns boolean.
 * If true then array differs from the one that was used previously, false otherwise.
 * @memberof Mojito.Presentation.Hooks
 */
export const useArrayComparator = initialArray => {
    const cache = useRef(initialArray);
    return useCallback((arr = []) => {
        const prevArray = cache.current;
        cache.current = arr;
        return !arraysAreEqual(prevArray, arr);
    }, []);
};

/**
 * Hook accepts boolean flag and resolves to false if flag is false.
 * Once flag is true hook will catch this state and will be always resolved to true
 * on each forthcoming render.
 *
 * @function useIrreversibleTrue
 *
 * @param {boolean} flag - Boolean flag.
 *
 * @returns {boolean} Boolean result.
 * @memberof Mojito.Presentation.Hooks
 */
export const useIrreversibleTrue = flag => {
    const truthfulRef = useRef(false);
    return useMemo(() => {
        if (flag || truthfulRef.current) {
            truthfulRef.current = true;
            return true;
        }
        return false;
    }, [flag]);
};

/**
 * Hook that behaves like useEffect, but is dormant until component has been mounted.
 *
 * @function useEffectOnUpdate
 *
 * @param {Function} callback - Callback that only is invoked on dependency change after the component mounted.
 * @param {Array} dependencies - List of dependencies that will trigger callback on change.
 * @memberof Mojito.Presentation.Hooks
 */
export const useEffectOnUpdate = (callback, dependencies) => {
    const mounted = useRef(false);

    useEffect(() => {
        if (mounted.current) {
            return callback();
        }
        mounted.current = true;
    }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Hook that combines multiple refs (object or callback) into a single ref callback.
 * Useful when you need to assign multiple refs to the same DOM element.
 *
 * @function useCombinedRefs
 * @param {...(Function|object)} refs - Refs to be combined (can be callback refs or ref objects).
 * @returns {Function} Ref callback to be passed to a React element.
 * @memberof Mojito.Presentation.Hooks
 */
export const useCombinedRefs = (...refs) => {
    const targetRef = useRef();
    return useCallback(
        node => {
            targetRef.current = node;
            refs.filter(Boolean).forEach(ref => {
                if (typeof ref === 'function') {
                    ref(node);
                } else {
                    ref.current = node;
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [...refs]
    );
};

/**
 * Hook that detects when component appears in a viewport.
 *
 * @function useComponentVisibility
 *
 * @param {Function} callback - Callback that is invoked once component appears in a viewport.
 * @param {number} [threshold = 0] - The threshold at which the callback will be invoked. 0 - when it shows up partly, 1 - when it is 100% in view.
 * @param {Node} [root] - Observer root element to be used for intersection checking. If undefined, the browser viewport will be used.
 * @param {boolean} [once = true] - Flag indicating if callback should be invoked only once upon first visibility. Default is true.
 *
 * @returns {Function} Ref function to be passed as a component ref.
 * @memberof Mojito.Presentation.Hooks
 */
export const useComponentVisibility = (callback, threshold = 0, root = undefined, once = true) => {
    const observerRef = useRef();
    const componentRef = useRef();
    const resetObserver = useCallback(() => {
        observerRef.current?.disconnect();
        observerRef.current = undefined;
    }, []);

    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
        };
    }, []);

    return useCallback(
        node => {
            if (componentRef.current === node) {
                return;
            }
            resetObserver();
            componentRef.current = node;
            if (node && callback) {
                observerRef.current = new IntersectionObserver(
                    ([entry]) => {
                        if (entry.isIntersecting) {
                            callback(entry);
                            once && resetObserver();
                        }
                    },
                    { threshold, root }
                );
                observerRef.current.observe(node);
            }
        },
        [resetObserver, threshold, callback, root, once]
    );
};

const isAboveViewport = element => {
    const bottom = element?.getBoundingClientRect()?.bottom;
    return bottom < 0;
};

/**
 * Hook that detects when component appears in a viewport (see Mojito.Presentation.Hooks.useComponentVisibility) or appears to be above visible viewport area due to scrollRestoration browser feature.
 * The scroll restoration can happen for a page on back button click. The hook will listen for the first scroll event on Window object after observable component did mount.
 * If component is above viewable area then the callback will be triggered.
 *
 * @function useComponentVisibilityAfterScrollRestoration
 *
 * @param {Function} callback - Callback that is invoked once component appears in a viewport.
 * @param {number} [threshold = 0] - The threshold at which the callback will be invoked. 0 - when it shows up partly, 1 - when it is 100% in view.
 *
 * @returns {Function} Ref function to be passed as a component ref.
 * @memberof Mojito.Presentation.Hooks
 */
export const useComponentVisibilityAfterScrollRestoration = (callback, threshold = 0) => {
    const refFunction = useComponentVisibility(callback, threshold);
    const elementRef = useRef();

    // For case when user use back navigation - browser restores scroll position that can move viewport all the way down
    // and due to that the component above it stays invisible, that is why we have to implement this hack with scroll
    // listening, as this is the only event that is dispatched in this case.
    // We can avoid this hack if we take a decision to disable scrollRestoration feature by setting
    // history.scrollRestoration = false on a global level
    const onScroll = useCallback(() => {
        isAboveViewport(elementRef.current) && callback();
        window.removeEventListener('scroll', onScroll);
    }, [callback]);

    return useCallback(
        element => {
            refFunction(element);
            elementRef.current = element;
            element && window.addEventListener('scroll', onScroll);
        },
        [refFunction, onScroll]
    );
};

/**
 * Hook that scrolls the component into view.
 *
 * @function useScrollIntoView
 *
 * @param {object} [options] - Scroll into view options.
 * @param {boolean} [shouldScroll = true] - Flag indicating if hook should scroll element into view once it is mounted.
 *
 * @returns {object} Ref to be passed as a component ref.
 * @memberof Mojito.Presentation.Hooks
 */
export function useScrollIntoView(options, shouldScroll = true) {
    const elementRef = useRef();
    const doScroll = useCallback(
        element => {
            if (element) {
                const scrollOptions = options || { block: 'nearest' };
                element.scrollIntoView(scrollOptions);
            }
        },
        [options]
    );
    useEffect(() => {
        shouldScroll && doScroll(elementRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldScroll]);

    return useCallback(
        element => {
            if (elementRef.current !== element) {
                shouldScroll && doScroll(element);
                elementRef.current = element;
            }
        },
        [doScroll, shouldScroll]
    );
}

const DEFAULT_LONG_PRESS_DURATION = 500;

/**
 * Hook that handles long press events on touch devices.
 *
 * @param {Function} ref - Function to be passed as a component ref.
 * @param {Function} onLongPress - Long press callback.
 * @param {number} [duration = 500] - Long press duration in milliseconds.
 *
 * @returns {object} Ref to be passed as a component ref.
 * @memberof Mojito.Presentation.Hooks
 */
export const useLongPress = (ref, onLongPress, duration = DEFAULT_LONG_PRESS_DURATION) => {
    const timeoutRef = useRef(null);
    const longPressTriggered = useRef(false);

    const startPressHandler = useCallback(() => {
        longPressTriggered.current = false;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            longPressTriggered.current = true;
            onLongPress();
        }, duration);
    }, [duration, onLongPress]);

    const endPressHandler = useCallback(event => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (longPressTriggered.current) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    }, []);

    const preventContextMenu = useCallback(event => {
        event.preventDefault();
    }, []);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        element.addEventListener('touchstart', startPressHandler);
        element.addEventListener('touchend', endPressHandler);
        element.addEventListener('touchcancel', endPressHandler);
        // prevent android context menu on long press
        element.addEventListener('contextmenu', preventContextMenu);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            element.removeEventListener('touchstart', startPressHandler);
            element.removeEventListener('touchend', endPressHandler);
            element.removeEventListener('touchcancel', endPressHandler);
            element.removeEventListener('contextmenu', preventContextMenu);
        };
    }, [startPressHandler, endPressHandler, preventContextMenu, ref]);

    return ref;
};

/**
 * Hook that returns translates url params.
 *
 * @function useLocalizedParams
 *
 * @returns {object} Translated params object.
 * @memberof Mojito.Presentation.Hooks
 */
export function useLocalizedParams() {
    const { routeResolver } = useAppContext();
    const { params = {} } = useRoute();
    return useMemo(
        () => mapValues(params, (value, name) => routeResolver.decodeParam(name, value) || value),
        [params, routeResolver]
    );
}

/**
 * Hook that returns the current location hash and reacts to hash changes.
 *
 * @function useLocationHash
 *
 * @returns {string} Current location hash (without the leading '#').
 * @memberof Mojito.Presentation.Hooks
 */
export function useLocationHash() {
    const [hash, setHash] = useState(() => window.location.hash.slice(1));

    useEffect(() => {
        const onHashChange = () => setHash(window.location.hash.slice(1));
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    return hash;
}
